<script setup lang="ts">
import { ref, onBeforeUnmount } from 'vue';

defineProps({
  heightSize: {
    type: String,
    default: 'auto',
  },

  widthSize: {
    type: String,
    default: '100%',
  },

  contentStyle: {
    type: String,
    default: '',
  },

  visible: {
    type: Boolean,
    default: false,
  },

  customClasses: {
    type: String,
    default: '',
  },

  noPadding: {
    type: Boolean,
    default: false,
  },
});

const thumbStyle: Partial<CSSStyleDeclaration> = {
  borderRadius: '5px',
  backgroundColor: '#a5b7c0',
  width: '5px',
  opacity: '0.5',
};

const barStyle: Partial<CSSStyleDeclaration> = {
  borderRadius: '10px',
  backgroundColor: '#a5b7c0',
  width: '5px',
  opacity: '0.2',
};

const scrollAreaRef = ref();
const rafId = ref<number | null>(null);

const scrollToBottom = () => {
  if (!scrollAreaRef.value?.getScrollTarget) return;
  const el = scrollAreaRef.value.getScrollTarget();
  if (!el) return;

  const maxAttempts = 10;
  const stabilityThreshold = 2;
  let attempts = 0;
  let stableFrames = 0;
  let lastHeight = 0;

  if (rafId.value !== null) {
    cancelAnimationFrame(rafId.value);
    rafId.value = null;
  }

  const tryScroll = () => {
    const target = scrollAreaRef.value?.getScrollTarget?.();
    if (!target) {
      rafId.value = null;
      return;
    }

    const currentHeight = target.scrollHeight;
    if (currentHeight === lastHeight) {
      stableFrames += 1;
    } else {
      stableFrames = 0;
      lastHeight = currentHeight;
    }

    if (stableFrames >= stabilityThreshold || attempts >= maxAttempts) {
      const maxScrollTop = Math.max(0, target.scrollHeight - target.clientHeight);
      scrollAreaRef.value?.setScrollPosition?.('vertical', maxScrollTop);
      rafId.value = null;
      return;
    }

    attempts += 1;
    rafId.value = requestAnimationFrame(tryScroll);
  };

  rafId.value = requestAnimationFrame(tryScroll);
};

onBeforeUnmount(() => {
  if (rafId.value !== null) {
    cancelAnimationFrame(rafId.value);
    rafId.value = null;
  }
});

function getScrollTarget() {
  return scrollAreaRef.value?.getScrollTarget?.();
}

defineExpose({
  scrollToBottom,
  getScrollTarget,
});
</script>

<template>
  <q-scroll-area
    ref="scrollAreaRef"
    :style="{ height: heightSize, width: widthSize }"
    :bar-style="barStyle"
    :thumb-style="thumbStyle"
    :vertical-offset="[5, 5]"
    :content-style="contentStyle"
    :content-active-style="contentStyle"
    :visible="visible"
    :class="[customClasses, { 'q-pr-md': !noPadding }]"
  >
    <slot name="default" />
  </q-scroll-area>
</template>

<style lang="scss" scoped>
:deep(.q-scrollarea__bar) {
  right: 5px !important;
}

:deep(.q-scrollarea__thumb) {
  right: 5px !important;
}
</style>
